import { ComponentPropsWithoutRef, ForwardedRef, forwardRef } from 'react';
import { Button } from '@givebutter/primitives/button';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CollapsibleProps, CollapsibleTriggerProps } from '@radix-ui/react-collapsible';
import * as Collapsible from '@radix-ui/react-collapsible';
import { twMerge } from 'tailwind-merge';

interface EditableInputProps extends CollapsibleProps {}

const EditableInput = (props: EditableInputProps) => {
    const { className, ...rest } = props;

    return <Collapsible.Root className={twMerge('group', className)} {...rest} />;
};

interface EditableInputContentProps extends ComponentPropsWithoutRef<'div'> {}
const EditableInputContent = (props: EditableInputContentProps) => {
    const { className, children, ...rest } = props;

    return (
        <Collapsible.Content
            className={twMerge('radix-state-open:flex flex-col gap-2', className)}
            {...rest}
        >
            {children}
        </Collapsible.Content>
    );
};

interface EditableInputActionsContainerProps extends ComponentPropsWithoutRef<'div'> {}
const EditableInputActionsContainer = (props: EditableInputActionsContainerProps) => {
    const { className, ...rest } = props;

    return <div className={twMerge('flex gap-2 self-start', className)} {...rest} />;
};

interface EditableInputCancelProps extends CollapsibleTriggerProps {}
const EditableInputCancel = (props: EditableInputCancelProps) => {
    const { className, children, ...rest } = props;

    return (
        <Collapsible.Trigger
            asChild={!children}
            className={twMerge('radix-state-closed:hidden', className)}
            {...rest}
        >
            {children || (
                <Button variant={'outline'} color={'neutral'}>
                    Cancel
                </Button>
            )}
        </Collapsible.Trigger>
    );
};

interface EditableInputReadOnlyProps extends ComponentPropsWithoutRef<'div'> {}
const EditableInputReadOnly = (props: EditableInputReadOnlyProps) => {
    const { className, children, ...rest } = props;

    return (
        <div
            className={twMerge('group-radix-state-open:hidden flex items-center gap-2', className)}
            {...rest}
        >
            {children}
        </div>
    );
};

interface EditableInputReadOnlyEditTriggerProps extends CollapsibleTriggerProps {}

const EditableInputReadOnlyEditTrigger = forwardRef(
    (props: EditableInputReadOnlyEditTriggerProps, ref: ForwardedRef<HTMLButtonElement>) => {
        const { className, children, ...rest } = props;

        return (
            <Collapsible.Trigger
                className={twMerge('radix-state-open:hidden', className)}
                ref={ref}
                {...rest}
            >
                {children || (
                    <>
                        <span className="sr-only">Edit</span>
                        <FontAwesomeIcon
                            size={'sm'}
                            className={'font-medium text-gray-400'}
                            icon={faEdit}
                        />
                    </>
                )}
            </Collapsible.Trigger>
        );
    },
);

const Root = EditableInput;
const Content = EditableInputContent;
const ActionsContainer = EditableInputActionsContainer;
const Cancel = EditableInputCancel;
const ReadOnly = EditableInputReadOnly;
const ReadOnlyEditTrigger = EditableInputReadOnlyEditTrigger;

export {
    EditableInput,
    EditableInputContent,
    EditableInputActionsContainer,
    EditableInputCancel,
    EditableInputReadOnly,
    EditableInputReadOnlyEditTrigger,
    //
    Root,
    Content,
    ActionsContainer,
    Cancel,
    ReadOnly,
    ReadOnlyEditTrigger,
};
